/**
 * Languages a report can be translated into (#1442): any ISO 639-1 code, not only
 * the instance languages fi / en / sv. The instance language itself stays fi / en / sv
 * (instanceLanguage.js); this list is for report translations only.
 *
 * Two byte-identical copies: packages/shared/ (display) and apps/backend/src/shared/
 * (Railway builds the backend from its own folder). `node scripts/sync-report-ui-strings.js`
 * fails on drift.
 *
 * `sv` keeps meaning Finland Swedish (sv-FI), as everywhere else in Kuutio.
 * Unlike normalizeInstanceLanguage, nothing here falls back to Finnish: an unknown
 * code is null, so a request for one is refused instead of quietly becoming `fi`.
 */
import { getResponseLanguageName } from './instanceLanguage.js';

// ISO 639-1
export const REPORT_LANGUAGE_CODES = [
  'aa', 'ab', 'ae', 'af', 'ak', 'am', 'an', 'ar', 'as', 'av', 'ay', 'az',
  'ba', 'be', 'bg', 'bi', 'bm', 'bn', 'bo', 'br', 'bs',
  'ca', 'ce', 'ch', 'co', 'cr', 'cs', 'cu', 'cv', 'cy',
  'da', 'de', 'dv', 'dz',
  'ee', 'el', 'en', 'eo', 'es', 'et', 'eu',
  'fa', 'ff', 'fi', 'fj', 'fo', 'fr', 'fy',
  'ga', 'gd', 'gl', 'gn', 'gu', 'gv',
  'ha', 'he', 'hi', 'ho', 'hr', 'ht', 'hu', 'hy', 'hz',
  'ia', 'id', 'ie', 'ig', 'ii', 'ik', 'io', 'is', 'it', 'iu',
  'ja', 'jv',
  'ka', 'kg', 'ki', 'kj', 'kk', 'kl', 'km', 'kn', 'ko', 'kr', 'ks', 'ku', 'kv', 'kw', 'ky',
  'la', 'lb', 'lg', 'li', 'ln', 'lo', 'lt', 'lu', 'lv',
  'mg', 'mh', 'mi', 'mk', 'ml', 'mn', 'mr', 'ms', 'mt', 'my',
  'na', 'nb', 'nd', 'ne', 'ng', 'nl', 'nn', 'no', 'nr', 'nv', 'ny',
  'oc', 'oj', 'om', 'or', 'os',
  'pa', 'pi', 'pl', 'ps', 'pt',
  'qu',
  'rm', 'rn', 'ro', 'ru', 'rw',
  'sa', 'sc', 'sd', 'se', 'sg', 'si', 'sk', 'sl', 'sm', 'sn', 'so', 'sq', 'sr', 'ss', 'st', 'su', 'sv', 'sw',
  'ta', 'te', 'tg', 'th', 'ti', 'tk', 'tl', 'tn', 'to', 'tr', 'ts', 'tt', 'tw', 'ty',
  'ug', 'uk', 'ur', 'uz',
  've', 'vi', 'vo',
  'wa', 'wo',
  'xh',
  'yi', 'yo',
  'za', 'zh', 'zu',
];

const CODES = new Set(REPORT_LANGUAGE_CODES);

/** Pinned at the top of the admin's language picker. */
export const COMMON_REPORT_LANGUAGES = ['en', 'sv', 'fi', 'et'];

/** Languages whose interface text is hand-written in the display's locale files. */
export const STATIC_UI_LANGUAGES = ['fi', 'en', 'sv'];

export function hasStaticUi(code) {
  return STATIC_UI_LANGUAGES.includes(code);
}

/** A known ISO 639-1 code in lower case, or null. `se` is Northern Sami, not Swedish. */
export function normalizeReportLanguage(code) {
  if (code == null) return null;
  const c = String(code).trim().toLowerCase();
  return CODES.has(c) ? c : null;
}

/**
 * The language's name in `inLang`, capitalised: languageName('et', 'fi') → 'Viro',
 * languageName('et', 'et') → 'Eesti', languageName('sv', 'en') → 'Swedish (Finland)'.
 */
export function languageName(code, inLang = 'en') {
  try {
    const name = new Intl.DisplayNames([inLang, 'en'], { type: 'language' }).of(code === 'sv' ? 'sv-FI' : code);
    if (!name || name === code) return String(code).toUpperCase();
    return name.charAt(0).toLocaleUpperCase(inLang) + name.slice(1);
  } catch {
    return String(code).toUpperCase();
  }
}

/** For {{language}} in the translation prompt: 'Finland Swedish (finlandssvenska, sv-FI)', 'Estonian (et)'. */
export function targetLanguageName(code) {
  if (hasStaticUi(code)) return getResponseLanguageName(code);
  return `${languageName(code, 'en')} (${code})`;
}

const DATE_LOCALES = { fi: 'fi-FI', en: 'en-GB', sv: 'sv-FI' };

/** BCP 47 locale for dates in a report shown in `code`. */
export function dateLocale(code) {
  return DATE_LOCALES[code] || code || 'fi-FI';
}
